import { useState } from 'react';

// Supports both controlled (value prop provided) and uncontrolled (internal state) usage.
// When controlled, the parent owns the value and updates flow through onChange.
// When uncontrolled, internal state is used and onChange is still called as a notification.
function useControllableState<T>({
  value,
  defaultValue,
  onChange,
}: {
  // If provided, the component is controlled and this value takes precedence.
  value?: T;
  // Used to seed internal state when uncontrolled; ignored once controlled.
  defaultValue: T;
  onChange?: (value: T) => void;
}) {
  const [internalValue, setInternalValue] = useState(defaultValue);

  // A defined value prop means the parent is driving state.
  const isControlled = value !== undefined;

  const currentValue = isControlled ? value : internalValue;

  const setValue = (nextValue: T) => {
    // Only update internal state when uncontrolled; controlled components re-render via the parent.
    if (!isControlled) {
      setInternalValue(nextValue);
    }

    onChange?.(nextValue);
  };

  return [currentValue, setValue] as const;
}

export default useControllableState;
